import {
  AfterContentInit,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input, OnDestroy,
  Output,
  QueryList,
  ViewChild
} from '@angular/core';
import {ListGroupComponent} from '../list-group/list-group.component';
import {ListKeyManager} from '@angular/cdk/a11y';
import {InputComponent} from '../input/input.component';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {ListGroupItemComponent} from '../list-group/list-group-item/list-group-item.component';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

@Component({
  selector: 'ds-combo-box-dropdown-option',
  templateUrl: '../list-group/list-group-item/list-group-item.component.html',
  styleUrls: ['../list-group/list-group-item/list-group-item.component.scss']
})
export class ComboBoxDropdownOptionComponent extends ListGroupItemComponent {

  constructor() {
    super();
  }
}

@Component({
  selector: 'ds-combo-box-dropdown-group',
  templateUrl: '../list-group/list-group.component.html',
  styleUrls: ['../list-group/list-group.component.scss']
})
export class ComboBoxDropdownGroupComponent extends ListGroupComponent implements OnDestroy {
  @ContentChildren(ComboBoxDropdownOptionComponent) listItems: QueryList<ComboBoxDropdownOptionComponent>;
  public keyboardManager: ListKeyManager<ComboBoxDropdownOptionComponent>;
}

@UntilDestroy()
@Component({
  selector: 'ds-combo-box-dropdown',
  templateUrl: './combo-box-dropdown.component.html',
  styleUrls: ['./combo-box-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ComboBoxDropdownComponent),
      multi: true
    }
  ]
})
export class ComboBoxDropdownComponent implements AfterContentInit, ControlValueAccessor, OnDestroy {

  @ContentChild(ComboBoxDropdownGroupComponent, {static: true}) comboBoxDropdownGroup: ComboBoxDropdownGroupComponent;
  @ViewChild(InputComponent) inputComponent: InputComponent;
  @ViewChild('select') selectInput: ElementRef;
  @Input() public id = '';
  @Input() public multiselect = false;
  @Input() searchInput = '';
  @Input() initialValue: string[] = [];
  @Input() searchPlaceholder = '';
  @Input() selectedHtml = '';
  @Input() showSearchBar = true;
  @Input() showClearButton = false;
  @Input() cdkTrapFocusEnabled = true;
  @Input() hideSearchPlaceholderWhenFilled = true;
  @Input() hideSearchPlaceholderWhenFocused = false;
  @Output() valueChange = new EventEmitter<string[]>();
  private onTouchedCallback: () => {};
  private onChangeCallback: (_: any) => {};
  private skipInitialValue: boolean = false;

  refresh$: Subject<boolean> = new Subject();

  get value(): string[] {
    if (this.comboBoxDropdownGroup?.listItems) {
      return this.comboBoxDropdownGroup.listItems
        .filter((element) => element.isActive)
        .map((element) => element.value);
    }
    return [];
  }

  set value(valueArray: string[]) {
    if (valueArray?.length) {
      this.setValue(valueArray);
    }
  }

  ngAfterContentInit(): void {
    if (this.comboBoxDropdownGroup?.listItems) {
      this.comboBoxDropdownGroup.listItems.changes
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.subscribeOptionClickEvent();
          this.setInitialValue();
        });
    }
    this.subscribeOptionClickEvent();
    this.setInitialValue();
  }

  ngOnDestroy() {
    this.refresh$.next(true);
    this.refresh$.complete();
  }

  setValue(valueArray: string[], newValue = true) { // NOSONAR
    if (valueArray?.length) {
      this.skipInitialValue = true;
      for (const value of valueArray) { // NOSONAR
        if (this.comboBoxDropdownGroup?.listItems) {
          const selectedOption = this.comboBoxDropdownGroup.listItems.find((option) => option.value === value);
          if (selectedOption) {
            if (this.multiselect) {
              selectedOption.isActive = newValue;
            } else {
              this.comboBoxDropdownGroup.listItems.forEach(option => {
                option.isActive = false;
              });
              selectedOption.isActive = true;
            }
          }
        }
      }
      this.valueChange.emit(this.value);
      if (this.onChangeCallback) {
        this.onChangeCallback(this.value);
      }
    } else {
      if (this.comboBoxDropdownGroup?.listItems) {
        this.comboBoxDropdownGroup.listItems.forEach(option => {
          option.isActive = false;
        });
      }
    }
  }

  /**
   * Reacts on [(ngModel)] changes.
   * Takes a list of the values of the dropdown list items that should be checked.
   *
   * @param value value passed into [(ngModel)]. List of checked values.
   */
  writeValue(value: string[]) {
    this.value = value;
  }

  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  public setFocusState(isFocused: boolean = true) {
    if (this.showSearchBar) {
      this.inputComponent.setFocusState(isFocused);
    } else if (this.comboBoxDropdownGroup) {
      this.comboBoxDropdownGroup.focus();
    }
  }

  public resetSearch(): void {
    if (this.showSearchBar) {
      this.inputComponent.value = '';
      this.searchInput = '';
      this.searchInputChanged();
      if (this.comboBoxDropdownGroup?.keyboardManager) {
        this.comboBoxDropdownGroup.keyboardManager.setActiveItem(-1);
      }
    }
  }

  /**
   * Called when an ds-combo-box-dropdown-option element is clicked.
   *
   * @param value Identifier of the clicked ds-combo-box-dropdown-option.
   */
  onElementClick(value: string) {
    let isActive = true;
    if (this.multiselect) {
      const isAlreadySelected: boolean = this.value.some(v => v === value);
      isActive = !isAlreadySelected;
    }
    this.setValue([value], isActive);
  }

  setInitialValue(): void {
    setTimeout(() => {
      if (!this.skipInitialValue) {
        this.value = this.initialValue;
      }
    }, 0);
  }

  searchInputChanged() {
    if (this.comboBoxDropdownGroup?.listItems) {
      this.comboBoxDropdownGroup.listItems.forEach(option => {
        option.disabled = this.searchInput.length > 0 && option.getLabel().toLowerCase().indexOf(this.searchInput.toLowerCase()) === -1;
      });
      this.selectFirst();
    }
  }

  isNavigationKey(code) {
    return code === 'Tab' || code === 'ArrowDown' || code === 'ArrowUp' || code === 'Tab' || code === 'Enter';
  }

  handleKeyInput(event) {
    if (this.comboBoxDropdownGroup && this.isNavigationKey(event.code)) {
      this.comboBoxDropdownGroup.handleKeyInput(event);
    }
  }

  selectFirst() {
    if (this.comboBoxDropdownGroup?.keyboardManager) {
      this.comboBoxDropdownGroup.keyboardManager.setFirstItemActive();
    }
  }

  private subscribeOptionClickEvent(): void {
    this.refresh$.next(true);
    this.comboBoxDropdownGroup.listItems.forEach(cp => cp.elementClick.pipe(takeUntil(this.refresh$), untilDestroyed(this)).subscribe((value) => {
      this.onElementClick(value);
    }));
  }
}
